import { gql } from '@apollo/client';
import { AddIcon, InfoIcon, RemoveIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { Form, FormTagsInput, FormTextInput, Select, Slider, TextInput } from '@components/form';
import { useGraphqlPagination } from '@components/infiniteScroll';
import { Group } from '@components/layout';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  Dataset,
  GetDatasetsForMixQuery,
  useCreateMixedDatasetMutation,
  useGetDatasetsForMixQuery,
} from '@generated/graphql';
import { Divider, MenuItem, SelectChangeEvent, Stack, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { DatasetDetailsAccordion } from './DatasetDetailsAccordion';

gql`
  query GetDatasetsForMix($workspaceId: String!, $cursor: String, $limit: Int) {
    getDatasets(workspaceId: $workspaceId, cursor: $cursor, limit: $limit) {
      classes
      datasetId
      files
      name
    }
  }
`;

const validationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required('Name is required')
    .max(128, 'Must be less than 128 characters'),
  description: yup.string(),
  seed: yup
    .number()
    .required('Seed number is required')
    .min(0, 'Seed must be more then or equal to 0')
    .max(2147483647, 'Seed must be less than 2147483648'),
  tags: yup.array().of(yup.string()),
});

type MixDatasetsModalProps = {
  workspaceId: string;
  selectedDatasets: string[];
  onClose: VoidFunction;
  refetch: VoidFunction;
};

type MixPart = {
  datasetId: string;
  maxCount: number;
  classes: string[];
};

export const MixDatasetsModal = ({
  workspaceId,
  selectedDatasets,
  refetch,
  onClose,
}: MixDatasetsModalProps) => {
  const { data: datasetsData, fetchMore: fetchMoreDatasets } = useGraphqlPagination<
    GetDatasetsForMixQuery,
    Dataset
  >(
    useGetDatasetsForMixQuery({ variables: { workspaceId, limit: 100 } }),
    'getDatasets',
    'datasetId',
  );
  const [createMixedDataset] = useCreateMixedDatasetMutation();

  const [defaultSeed] = useState(Number(Math.floor(Math.random() * 1000000000)));
  const [parts, setParts] = useState<MixPart[]>(() => {
    const initialParts =
      selectedDatasets?.map((datasetId) => ({
        datasetId,
        maxCount: 1,
        classes: [],
      })) || [];
    while (initialParts.length < 2) {
      initialParts.push({ datasetId: '', maxCount: 0, classes: [] });
    }
    return initialParts;
  });

  const updatePart = (index: number, part: MixPart) => {
    setParts((prev) => {
      const newParts = [...prev];
      newParts[index] = part;
      return newParts;
    });
  };

  const handleSubmit = async ({
    description,
    name,
    seed,
    tags,
  }: {
    description: string;
    name: string;
    seed: number;
    tags: string[];
  }) => {
    const variables: {
      workspaceId: string;
      name: string;
      description: string;
      seed?: number;
      tags?: string[];
      parameters: string;
    } = { workspaceId, name, description, seed, tags, parameters: '' };
    const parameters: Record<string, { samples: number; classes?: string[] }> = {};
    parts.forEach((part) => {
      if (part.datasetId === '') {
        return;
      }
      parameters[part.datasetId] = { samples: part.maxCount };
      if (part.classes.length > 0) {
        parameters[part.datasetId].classes = part.classes;
      }
    });
    variables.parameters = JSON.stringify(parameters);
    await createMixedDataset({ variables });
    if (refetch) {
      void refetch();
    }
    onClose();
  };

  const errorMessage = parts.some((part) => part.datasetId === '')
    ? 'All parts must have a dataset'
    : undefined;

  return (
    <Form
      enableReinitialize
      initialValues={{
        name: '',
        description: '',
        seed: defaultSeed,
        tags: [],
      }}
      validationSchema={validationSchema}
      validateOnBlur
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Stack sx={{ maxHeight: '75vh', overflow: 'hidden' }}>
          <FormTextInput
            label="Dataset name"
            name="name"
            required
            sx={{ minWidth: '40ch', maxWidth: '50%' }}
          />
          <FormTextInput
            label="Description"
            name="description"
            sx={{ minWidth: '40ch', maxWidth: '50%' }}
            multiline
            rows={3}
          />
          <FormTextInput
            label="Seed"
            type="number"
            name="seed"
            sx={{ minWidth: '40ch', maxWidth: '50%' }}
          />
          <FormTagsInput name="tags" fullwidth />
          <Divider sx={{ mb: 3 }} />
          <Stack sx={{ overflowY: 'auto', gap: 3 }}>
            {parts.map((part, index) => (
              <MixPartForm
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                index={index + 1}
                part={part}
                datasets={datasetsData}
                fetchMoreDatasets={fetchMoreDatasets}
                onChange={(update) => updatePart(index, update)}
                onRemove={() => setParts((prev) => prev.filter((_, i) => i !== index))}
              />
            ))}
            <IconButton
              Icon={AddIcon}
              iconProps={{ circle: true }}
              size={24}
              sx={{ mx: 'auto', my: 2 }}
              onClick={() =>
                setParts((prev) => [
                  ...prev,
                  { datasetId: '', maxCount: 0, classes: [], allClasses: false },
                ])
              }
            />
          </Stack>
          <Group justifyContent="flex-end" gap={4} alignItems="center" mt={2}>
            <Tooltip title={errorMessage}>
              <div>
                <Button disabled={Boolean(errorMessage)} type="submit" loading={isSubmitting}>
                  Mix
                </Button>
              </div>
            </Tooltip>
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
          </Group>
        </Stack>
      )}
    </Form>
  );
};

type MixPartFormProps = {
  index: number;
  part: MixPart;
  datasets: Dataset[];
  fetchMoreDatasets: () => Promise<void>;
  onChange: (part: MixPart) => void;
  onRemove: () => void;
};

const MixPartForm = ({
  index,
  part,
  datasets,
  fetchMoreDatasets,
  onChange,
  onRemove,
}: MixPartFormProps) => {
  const { palette } = useTheme();
  const dataset = useMemo(
    () => datasets.find((d) => d.datasetId === part.datasetId),
    [part.datasetId, datasets],
  );
  useEffect(() => {
    if (dataset && onChange) {
      onChange({ ...part, maxCount: dataset?.files || 1 });
    }
    // Intent: notify the parent of `maxCount` exactly when the resolved dataset record
    // changes. `onChange` and `part` are intentionally omitted from deps — they're fresh
    // each parent render, and re-firing on those changes would either redundantly push
    // the same data up or risk an infinite loop (parent's onChange isn't memoized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);
  return (
    <Group
      sx={{
        position: 'relative',
        backgroundColor: palette.modal.background.card,
      }}
      alignItems="flex-start"
      py={4}
    >
      <Typography variant="body2" sx={{ mx: 4 }}>
        {index}
      </Typography>
      <Stack px={4} gap={4} sx={{ flex: 1 }}>
        <Select
          label="Dataset"
          value={part.datasetId}
          sx={{
            width: '70%',
            minWidth: '300px',
          }}
          fetchMore={fetchMoreDatasets}
          onChange={(e: SelectChangeEvent<string>) =>
            onChange({ datasetId: e.target.value, maxCount: 1, classes: [] })
          }
        >
          {datasets.map((option) => (
            <MenuItem key={option.datasetId} value={option.datasetId}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        <Group alignItems="flex-end" gap={9}>
          <Stack sx={{ width: 'calc( 70% - 20px)', minWidth: '300px' }}>
            <Typography
              variant="caption2"
              sx={{
                color: !dataset ? palette.input.label.disabled : undefined,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              Max sample count
              <Tooltip title="Maximum number of samples before applying additional filters">
                <InfoIcon size={16} />
              </Tooltip>
            </Typography>
            <Slider
              value={part.maxCount}
              min={1}
              max={dataset?.files || 1}
              onChange={(_, value) => onChange({ ...part, maxCount: value as number })}
              sx={{ ml: 4 }}
              disabled={!dataset}
            />
          </Stack>
          {dataset && (
            <Group alignItems="center" sx={{ flex: 1 }} gap={1}>
              <TextInput
                value={part.maxCount}
                min={1}
                max={dataset?.files || 1}
                onChange={(e) => onChange({ ...part, maxCount: Number(e.target.value) })}
                type="number"
                containerProps={{ sx: { flex: 1 } }}
              />
              <Typography variant="body2" sx={{ lineHeight: '30px', whiteSpace: 'nowrap' }}>
                / {dataset.files} images
              </Typography>
            </Group>
          )}
        </Group>
        <DatasetDetailsAccordion
          title="Additional filters"
          disabled={!dataset}
          details={
            dataset?.classes?.length === 0 ? (
              <Typography variant="caption2">No classes available for selection</Typography>
            ) : (
              <Group justifyContent="flex-start" alignItems="flex-end" gap={4} px={2}>
                <Select
                  label="Included classes"
                  labelInfo="Only images that include one or more of the selected classes will be sampled for the new dataset. If no classes are selected, all images will be sampled."
                  value={part.classes}
                  multiple
                  sx={{
                    minWidth: '300px',
                  }}
                  fetchMore={fetchMoreDatasets}
                  onChange={(e: SelectChangeEvent<string[]>) =>
                    onChange({ ...part, classes: e.target.value as string[] })
                  }
                  disabled={!dataset}
                >
                  {dataset?.classes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Group>
            )
          }
        />
      </Stack>
      {index > 1 && (
        <IconButton
          Icon={RemoveIcon}
          iconProps={{ circle: true }}
          size={24}
          onClick={onRemove}
          sx={{ position: 'absolute', right: 4, top: 4 }}
        />
      )}
    </Group>
  );
};
